// Authentication Context
import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { onAuthStateChanged, signInWithGoogle, signOut, initAuth } from '../services/auth.js';
import { getProStatus, getPlanDisplayName, isExpired } from '../services/pro-status.js';
import { auth as firebaseAuth } from '../services/firebase-config.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [proStatus, setProStatus] = useState(null);
    const [proLoading, setProLoading] = useState(false);

    // Fetch pro status for a given user
    const fetchProStatus = useCallback(async (uid) => {
        if (!uid) {
            setProStatus(null);
            return;
        }
        setProLoading(true);
        try {
            const status = await getProStatus(uid);
            setProStatus(status);
        } catch (err) {
            console.error('Error fetching pro status:', err);
            setProStatus({ isPro: false, proType: 'free' });
        } finally {
            setProLoading(false);
        }
    }, []);

    // Refresh pro status (can be called by child components)
    const refreshProStatus = useCallback(async () => {
        if (user?.uid) {
            await fetchProStatus(user.uid);
        }
    }, [user?.uid, fetchProStatus]);

    useEffect(() => {
        // Initialize auth and check for existing user
        initAuth().then((currentUser) => {
            setUser(currentUser);
            setLoading(false);
            if (currentUser?.uid) {
                fetchProStatus(currentUser.uid);
            }
        });

        // Subscribe to auth state changes
        const unsubscribe = onAuthStateChanged((user) => {
            setUser(user);
            setLoading(false);
            if (user?.uid) {
                fetchProStatus(user.uid);
            } else {
                setProStatus(null);
            }
        });

        // Re-check auth state when the page regains focus/visibility.
        // This catches sign-outs that happened in the extension sidepanel
        // or another tab while this page was in the background.
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const currentFirebaseUser = firebaseAuth.currentUser;
                setUser((prevUser) => {
                    // If Firebase says no user but we think there is one → sign out
                    if (!currentFirebaseUser && prevUser) {
                        console.log('[AuthContext] Detected sign-out on visibility change');
                        setProStatus(null);
                        return null;
                    }
                    // If Firebase says there's a user but we don't have one → sign in
                    if (currentFirebaseUser && !prevUser) {
                        console.log('[AuthContext] Detected sign-in on visibility change');
                        const newUser = {
                            uid: currentFirebaseUser.uid,
                            email: currentFirebaseUser.email,
                            displayName: currentFirebaseUser.displayName,
                            photoURL: currentFirebaseUser.photoURL,
                        };
                        fetchProStatus(currentFirebaseUser.uid);
                        return newUser;
                    }
                    return prevUser;
                });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            unsubscribe();
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [fetchProStatus]);

    const login = async () => {
        try {
            setError(null);
            setLoading(true);
            const user = await signInWithGoogle();
            setUser(user);
            // Fetch pro status after login
            if (user?.uid) {
                await fetchProStatus(user.uid);
            }
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        try {
            setLoading(true);
            await signOut();
            setUser(null);
            setProStatus(null);
        } catch (err) {
            setError(err.message);
            throw err;
        } finally {
            setLoading(false);
        }
    };

    const value = {
        user,
        loading,
        error,
        login,
        logout,
        isAuthenticated: !!user,
        // Pro status
        proStatus,
        proLoading,
        isPro: proStatus?.isPro === true && !isExpired(proStatus),
        planName: getPlanDisplayName(proStatus?.proType),
        refreshProStatus,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
